import { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import TimeFilter from "../components/TimeFilter";
import MonthRangePicker from "../components/MonthRangePicker";
import StatCard from "../components/StatCard";
import DashboardSection from "../components/DashboardSection";
import ErrorState from "../components/ErrorState";
import { SkeletonStatGrid } from "../components/Skeleton";
import { WalletIcon, TrendUpIcon } from "../components/icons";

import {
  loansToday,
  loansYesterday,
  loansLast7,
  loansLast30,
  loansMonthlyRange
} from "../api/loan.api";

export default function Loans() {
  const [filter, setFilter] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (filter !== "custom") loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      let res;
      if (filter === "today") res = await loansToday();
      else if (filter === "yesterday") res = await loansYesterday();
      else if (filter === "last7") res = await loansLast7();
      else if (filter === "last30") res = await loansLast30();
      setData(res.data);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const loadCustom = async ({ fromYear, fromMonth, toYear, toMonth }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await loansMonthlyRange({
        start_year: fromYear,
        start_month: fromMonth,
        end_year: toYear,
        end_month: toMonth
      });
      setData(res.data);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const firstLoans = data?.first_loan && typeof data.first_loan === "object" ? data.first_loan : {};
  const normalLoans = data?.normal_loan && typeof data.normal_loan === "object" ? data.normal_loan : {};

  return (
    <div className="page-container">
      <PageHeader
        title="Loans Performance"
        subtitle="First-loan and repeat-loan performance and amounts for the selected range."
      />

      <div className="toolbar">
        <TimeFilter value={filter} onChange={setFilter} />
      </div>
      <MonthRangePicker open={filter === "custom"} onApply={loadCustom} />

      {error ? (
        <ErrorState message={error} onRetry={filter === "custom" ? undefined : loadData} />
      ) : (
        <>
          <DashboardSection title="First Loans">
            {loading || !data ? (
              <SkeletonStatGrid count={4} />
            ) : Object.keys(firstLoans).length === 0 ? (
              <div className="empty-state">No first loan records for this range.</div>
            ) : (
              <div className="grid">
                {Object.entries(firstLoans).map(([k, v], i) => (
                  <StatCard
                    key={k}
                    title={k.replace(/_/g, " ")}
                    value={v}
                    isCurrency={/amount|disburs|total/i.test(k) && typeof v === "number"}
                    icon={/amount/i.test(k) ? TrendUpIcon : WalletIcon}
                    index={i}
                  />
                ))}
              </div>
            )}
          </DashboardSection>

          <DashboardSection title="Normal / Repeat Loans">
            {loading || !data ? (
              <SkeletonStatGrid count={4} />
            ) : Object.keys(normalLoans).length === 0 ? (
              <div className="empty-state">No repeat loan records for this range.</div>
            ) : (
              <div className="grid">
                {Object.entries(normalLoans).map(([k, v], i) => (
                  <StatCard
                    key={k}
                    title={k.replace(/_/g, " ")}
                    value={v}
                    isCurrency={/amount|disburs|total/i.test(k) && typeof v === "number"}
                    icon={/amount/i.test(k) ? TrendUpIcon : WalletIcon}
                    index={i}
                  />
                ))}
              </div>
            )}
          </DashboardSection>
        </>
      )}
    </div>
  );
}
